import config from "./config";

/**
 * Decodes a JWT token payload without making an API call.
 * Returns null if the token is malformed.
 */
export const decodeJwtPayload = (
  token: string,
): Record<string, unknown> | null => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
};

/**
 * Checks if a JWT token is expired based on the `exp` claim.
 * Returns true if the token is expired or malformed.
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return true;

  // exp is in seconds, Date.now() is in milliseconds
  return payload.exp * 1000 < Date.now();
};

/**
 * Gets the JWT token from localStorage and returns it only if it's valid (not expired).
 * Returns null if no token exists or the token is expired.
 */
export const getValidToken = (): string | null => {
  const token = localStorage.getItem(config.jwt_key_name);
  if (!token || isTokenExpired(token)) return null;
  return token;
};

/**
 * True when user has a usable session: valid access token, or expired access with refresh.
 */
export const hasAuthSession = (): boolean => {
  const token = localStorage.getItem(config.jwt_key_name);
  const refreshToken = localStorage.getItem("refreshToken");
  if (!token) return false;
  if (!isTokenExpired(token)) return true;
  return Boolean(refreshToken);
};

/**
 * Clears all auth-related data from localStorage.
 */
export const clearAuthStorage = (): void => {
  localStorage.removeItem(config.jwt_key_name);
  localStorage.removeItem("refreshToken");
};
